import { useState, FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { apiClient } from '../lib/api';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await sendResetEmail();
  };

  const sendResetEmail = async () => {
    setError('');
    setIsLoading(true);

    try {
      await apiClient.post('/auth/password/forgot', { email });
      setSuccess(true);
      setResendTimer(60); // 60 second cooldown
    } catch (err: any) {
      setError(
        err.response?.data?.message || t('auth.forgotPassword.error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    await sendResetEmail();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">
            {t('auth.forgotPassword.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('auth.forgotPassword.subtitle')}
          </p>
        </div>

        {success ? (
          <div className="space-y-6">
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-200 mb-1">
                    {t('auth.forgotPassword.successTitle')}
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {t('auth.forgotPassword.successMessage')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('auth.forgotPassword.didntReceive')}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleResend}
                  disabled={resendTimer > 0 || isLoading}
                  className="w-full"
                >
                  {resendTimer > 0
                    ? t('auth.forgotPassword.resendTimer', { seconds: resendTimer })
                    : t('auth.forgotPassword.resend')}
                </Button>
              </div>

              <div className="pt-4 border-t-2 border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <a
                    href="/login"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
                  >
                    {t('auth.forgotPassword.backToLogin')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="email"
                label={t('auth.forgotPassword.email')}
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                  <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                isLoading={isLoading}
                className="w-full"
              >
                {t('auth.forgotPassword.submit')}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                <a
                  href="/login"
                  className="font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
                >
                  {t('auth.forgotPassword.backToLogin')}
                </a>
              </p>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
